import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import axios from '../../../../utils/axios';
import { DispositivelModel, EntidadeModel } from 'models/entityCore';
import { useSnackbar } from 'notistack';
import Modal, { BotaoSecundario, Campo } from 'components/gps/Modal';
import { Num } from 'components/gps';
import { AutoCompleteUnico, buscarPaginado } from 'components/gps/AutoComplete';

/* ============================================================================
   ModalTransferenciaAutomatica - transferir o aparelho de grupo pelo QR.

   Migrado do Dialog do MUI para o Modal do visual novo. TODA a mecanica foi
   mantida palavra por palavra: criacao da sessao, leitura do SSE linha a linha,
   deteccao de sucesso/erro pelos varios nomes que o backend usa
   (status|type|resultado|event, success|sucesso|finalizado|concluido) e o
   AbortController no fechamento. Nada disso e visual, e e o que faz a
   transferencia funcionar - reescrever seria arriscar o fluxo para ganhar nada.

   TRES COISAS QUE A VERSAO ANTIGA ESCONDIA E QUE AGORA APARECEM:

   1. ESCOLHER O GRUPO JA CRIA A SESSAO. Nao era um formulario com botao: o
      `onChange` do Autocomplete dispara POST /dispositivos/mudar-grupo-sessao na
      hora. Quem clicava no grupo errado ja tinha criado sessao. O comportamento
      continua (e o proposito do modal), mas agora esta escrito na tela, antes do
      seletor, em vez de ser surpresa.

   2. O `sessionId` era guardado em estado e NUNCA mostrado. E o unico dado que
      liga esta tela ao log do backend quando a transferencia falha; sem ele o
      suporte nao tem por onde procurar.

   3. O estado de progresso vinha como um paragrafo cinza igual a todos os outros.
      Agora sucesso, erro e espera tem tom proprio - a diferenca entre "aguardando
      o mobile" e "deu erro" precisa ser visivel sem ler.

   O fundo do QR fica BRANCO fixo, e nao `bg-surface`: no tema escuro um QR de
   modulos escuros sobre fundo escuro nao e lido por leitor nenhum.
   ============================================================================ */

type CriarSessaoResponse = {
  sessionId?: string;
  id?: string;
  codigo?: string;
  deviceId?: string;
  grupoId?: number;
  grupoNome?: string;
  expiresAt?: string;
  qrCode?: string;
  payload?: string;
  streamUrl?: string;
};

type Tom = 'espera' | 'ok' | 'erro';

export default function ModalTransferenciaAutomatica({
  open,
  setOpen,
  device,
}: {
  open: boolean;
  setOpen: any;
  device: DispositivelModel;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamFinalizadoRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [grupoSelecionado, setGrupoSelecionado] = useState<EntidadeModel | null>(null);
  const [statusMensagem, setStatusMensagem] = useState('');
  const [statusTom, setStatusTom] = useState<Tom>('espera');
  const [sessionIdAtual, setSessionIdAtual] = useState<string | null>(null);
  const [qrCodeAtual, setQrCodeAtual] = useState<string>('');
  const [payloadAtual, setPayloadAtual] = useState<string>('');
  const [expiresAtAtual, setExpiresAtAtual] = useState<string | null>(null);

  const closeStream = (shouldAbort = true) => {
    if (abortControllerRef.current) {
      if (shouldAbort) {
        try {
          abortControllerRef.current.abort();
        } catch {}
      }
      abortControllerRef.current = null;
    }
  };

  /* GRUPOS PAGINADOS, SO NA LUPA (21/10/2026). Abrir este modal baixava TODOS
     os grupos (rows=9999) antes de qualquer escolha. Mesmo endpoint e filtro
     (`status=-1`, todos os status), agora 20 por pagina no ModalSelecionar. */
  const buscarGrupos = useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/entidades/paginadoPrincipal?page=${pagina}&rows=${linhas}&valor=${encodeURIComponent(termo)}` +
          `&campo=nome&direcao=asc&ordenar=nome&status=-1`,
        (g) => ({ codigo: g.codigo, rotulo: g.nome, secundario: g.regional?.nome, dado: g })
      ),
    []
  );

  useEffect(() => {
    if (!open) return;

    setGrupoSelecionado(null);
    setStatusMensagem('');
    setStatusTom('espera');
    setSessionIdAtual(null);
    setQrCodeAtual('');
    setPayloadAtual('');
    setExpiresAtAtual(null);
    streamFinalizadoRef.current = false;
    closeStream();
  }, [device?.deviceId, open]);

  useEffect(() => () => closeStream(), []);

  /* So o QR DA SESSAO (30/09/2026). A API passou a exigir uma sessao ativa
     para transferir pelo app do aparelho - e a sessao que prova que um
     operador autorizado pediu esta transferencia. O QR montado aqui mesmo, sem
     sessao (enquanto ela era criada ou quando falhava), levaria o aparelho a
     "Nao foi possivel transferir". */
  const qrValue = useMemo(() => payloadAtual || '', [payloadAtual]);

  const handleClose = () => {
    streamFinalizadoRef.current = false;
    closeStream();
    setOpen(false);
  };

  const getSessionId = (data: CriarSessaoResponse | undefined) => {
    return data?.sessionId || data?.id || data?.codigo || null;
  };

  const handleFinalizacaoStream = (payload: any) => {
    const status = String(payload?.status || payload?.type || payload?.resultado || payload?.event || '').toLowerCase();
    const success =
      payload?.success === true ||
      status === 'success' ||
      status === 'sucesso' ||
      status === 'finalizado' ||
      status === 'concluido' ||
      status === 'concluído';
    const error = payload?.success === false || status === 'error' || status === 'erro';
    const message = payload?.message || payload?.mensagem || payload?.detail;

    if (success) {
      streamFinalizadoRef.current = true;
      enqueueSnackbar(message || 'Transferência automática finalizada com sucesso.', {
        variant: 'success',
        style: { whiteSpace: 'pre-line' },
      });
      setStatusTom('ok');
      setStatusMensagem(message || 'Transferência finalizada com sucesso.');
      closeStream(false);
      setOpen(false);
      return;
    }

    if (error) {
      streamFinalizadoRef.current = true;
      enqueueSnackbar(message || 'Ops ocorreu um erro ao finalizar a transferência automática.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
      setStatusTom('erro');
      setStatusMensagem(message || 'A transferência retornou erro.');
      closeStream(false);
      return;
    }

    if (message) {
      setStatusTom('espera');
      setStatusMensagem(message);
    }
  };

  const processarEventoSSE = (bloco: string) => {
    const linhas = bloco.split(/\r?\n/);
    let eventName = 'message';
    const dataLines: string[] = [];

    linhas.forEach((linha) => {
      if (linha.startsWith('event:')) {
        eventName = linha.slice(6).trim();
      }

      if (linha.startsWith('data:')) {
        dataLines.push(linha.slice(5).trim());
      }
    });

    const data = dataLines.join('\n');
    if (!data) return;

    try {
      const payload = JSON.parse(data);
      handleFinalizacaoStream({ ...payload, event: payload?.event || eventName });
    } catch {
      handleFinalizacaoStream({ message: data, event: eventName, status: eventName });
    }
  };

  const abrirStream = async (streamUrl: string) => {
    closeStream();
    streamFinalizadoRef.current = false;

    const token = window.localStorage.getItem('gpshub:accessToken');
    /* Mesma regra do baseURL em utils/axios: em desenvolvimento a base e a ORIGEM
       DA PAGINA, e nao a string fixa `http://localhost:3000` que o vite.config
       injeta - senao, acessando o front pelo IP da rede, este stream tentaria
       `localhost` na maquina de quem acessa. Aqui e `fetch` direto (nao passa pelo
       axios) por causa do corpo em streaming, entao a conta precisa ser repetida. */
    const baseAPI =
      process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
        ? window.location.origin
        : process.env.HOST_API_KEY || '';
    const streamUrlCompleta = streamUrl.startsWith('http') ? streamUrl : `${baseAPI}${streamUrl}`;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setStatusTom('espera');
      setStatusMensagem('Conectando ao acompanhamento da transferência...');

      const response = await fetch(streamUrlCompleta, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Falha ao conectar no stream (${response.status}).`);
      }

      if (!response.body) {
        throw new Error('Stream não retornou body para leitura.');
      }

      setStatusTom('espera');
      setStatusMensagem('Sessão conectada. Aguardando o mobile concluir a transferência.');

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const partes = buffer.split(/\r?\n\r?\n/);
        buffer = partes.pop() || '';

        partes.forEach((parte) => processarEventoSSE(parte));

        if (streamFinalizadoRef.current) {
          reader.cancel();
          break;
        }
      }

      if (buffer.trim()) {
        processarEventoSSE(buffer);
      }

      if (!streamFinalizadoRef.current && !controller.signal.aborted) {
        setStatusTom('erro');
        setStatusMensagem('A conexão do acompanhamento foi encerrada antes da confirmação final.');
        enqueueSnackbar('Ops ocorreu um erro ao acompanhar a transferência automática.', {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
      }
    } catch (error: any) {
      if (controller.signal.aborted || streamFinalizadoRef.current) return;

      setStatusTom('erro');
      setStatusMensagem('Não foi possível acompanhar o progresso da transferência.');
      enqueueSnackbar(error?.message || 'Ops ocorreu um erro ao acompanhar a transferência automática.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const handleSelecionarGrupo = async (value: EntidadeModel | null) => {
    setGrupoSelecionado(value);
    setStatusMensagem('');
    setStatusTom('espera');
    setSessionIdAtual(null);
    setQrCodeAtual('');
    setPayloadAtual('');
    setExpiresAtAtual(null);
    streamFinalizadoRef.current = false;
    closeStream();

    if (!value?.codigo || !device?.deviceId) return;

    try {
      setIsSubmitting(true);
      const response = await axios.post<CriarSessaoResponse>('/dispositivos/mudar-grupo-sessao', {
        grupoId: value.codigo,
        deviceId: device.deviceId,
      });

      const sessionId = getSessionId(response.data);
      const streamUrl = response.data?.streamUrl || null;

      if (!sessionId) {
        enqueueSnackbar('Sessão criada, mas o sessionId não foi retornado pela API.', {
          variant: 'warning',
          style: { whiteSpace: 'pre-line' },
        });
        setStatusTom('erro');
        setStatusMensagem('Sessão criada, mas não foi possível iniciar o acompanhamento.');
        return;
      }

      setSessionIdAtual(sessionId);
      setQrCodeAtual(response.data?.qrCode || '');
      setPayloadAtual(response.data?.payload || '');
      setExpiresAtAtual(response.data?.expiresAt || null);
      setStatusTom('espera');
      setStatusMensagem('Sessão iniciada. Aguardando o mobile concluir a transferência.');

      if (!streamUrl) {
        enqueueSnackbar('Sessão criada, mas a streamUrl não foi retornada pela API.', {
          variant: 'warning',
          style: { whiteSpace: 'pre-line' },
        });
        return;
      }

      abrirStream(streamUrl);
    } catch (error: any) {
      const message =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        'Ops ocorreu um erro ao iniciar a mudança de grupo.';

      enqueueSnackbar(message, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
      setStatusTom('erro');
      setStatusMensagem('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const TOM_CAIXA: Record<Tom, string> = {
    espera: 'border-info bg-info-soft',
    ok: 'border-ok bg-ok-soft',
    erro: 'border-danger bg-danger-soft',
  };

  return (
    <Modal
      aberto={open}
      titulo="Transferência de grupo automática"
      descricao={
        <>
          {device?.nome || 'Dispositivo'} · <span className="tabular-nums">{device?.deviceId || ' - '}</span>
        </>
      }
      largura="max-w-[560px]"
      onFechar={handleClose}
      rodape={<BotaoSecundario onClick={handleClose}>Fechar</BotaoSecundario>}
    >
      {/* O aviso vem ANTES do seletor: a sessao e criada ao CONFIRMAR o grupo no
          modal de selecao (21/10/2026), nao num botao depois. */}
      <div className="rounded-lg border border-warn bg-warn-soft p-2.5 text-[12px]">
        Ao confirmar o grupo, a sessão de transferência é <strong>criada na hora</strong> e o QR passa a valer por 10
        minutos.
      </div>

      {grupoSelecionado ? (
        <Campo bloco rotulo="Grupo de destino">
          <div className="flex items-center gap-2">
            <div className="flex h-9 flex-1 items-center rounded-lg border border-line bg-soft px-3 text-[12.5px] font-semibold">
              {grupoSelecionado.nome}
            </div>
            <BotaoSecundario onClick={() => handleSelecionarGrupo(null)}>Trocar</BotaoSecundario>
          </div>
        </Campo>
      ) : (
        <Campo bloco rotulo="Grupo de destino" obrigatorio dica="Clique na lupa para escolher. Confirmar cria a sessão.">
          {/* O registro do servidor (`dado`) vira o grupo escolhido, como antes. */}
          <AutoCompleteUnico
            selecionado={null}
            aoEscolher={(o) => o && !isSubmitting && handleSelecionarGrupo(o.dado)}
            buscar={buscarGrupos}
            titulo="Selecionar grupo de destino"
            placeholder="Nome do grupo (Enter ou lupa)"
            vazio="Nenhum grupo encontrado."
          />
        </Campo>
      )}

      {!!grupoSelecionado?.codigo && (!!qrCodeAtual || !!qrValue) && (
        <div className="flex flex-col items-center gap-2">
          {/* Fundo branco fixo: no tema escuro um QR escuro sobre fundo escuro nao
              e lido por leitor nenhum. */}
          <div className="rounded-lg border border-line bg-white p-3">
            {qrCodeAtual ? (
              <img src={qrCodeAtual} alt="QR Code da transferência" style={{ width: 240, height: 240 }} />
            ) : (
              <QRCode level="Q" style={{ width: 240 }} value={qrValue} />
            )}
          </div>
          <p className="text-center text-[12px] text-muted-foreground">
            Leia este QR Code no aplicativo do aparelho para transferir para <strong>{grupoSelecionado.nome}</strong>.
          </p>
        </div>
      )}

      {!!statusMensagem && (
        <div className={`rounded-lg border p-2.5 text-center text-[12px] ${TOM_CAIXA[statusTom]}`}>
          {statusMensagem}
        </div>
      )}

      {(sessionIdAtual || expiresAtAtual) && (
        <div className="rounded-lg border border-line p-2.5 text-[11px] text-muted-foreground">
          {sessionIdAtual && (
            <div className="flex justify-between gap-2">
              <span>Sessão</span>
              {/* Era guardado e nunca exibido; e o identificador que o suporte usa
                  para achar a transferencia no log do backend. */}
              <Num className="text-ink">{sessionIdAtual}</Num>
            </div>
          )}
          {expiresAtAtual && (
            <div className="mt-1 flex justify-between gap-2">
              <span>Expira em</span>
              <Num className="text-ink">{new Date(expiresAtAtual).toLocaleString('pt-BR')}</Num>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
